// Per-state memoisation for settlement-wide aggregates used inside modifier hooks (so a
// per-person hook does not rescan everyone, which would be O(n^2) per season; Section 2
// performance budget). Keyed by the state object: a phase's draft is a fresh key, so values
// are a snapshot taken at first use within that phase. Code that changes what a memo depends
// on mid-phase (a building completes or is damaged) calls `invalidateMemo`.

const cache = new WeakMap<object, Map<string, unknown>>();

export function memoByState<T>(state: object, key: string, compute: () => T): T {
  let entries = cache.get(state);
  if (entries === undefined) {
    entries = new Map();
    cache.set(state, entries);
  }
  if (entries.has(key)) return entries.get(key) as T;
  const value = compute();
  entries.set(key, value);
  return value;
}

/** Drop memoised values for a state whose key starts with `prefix` (all if omitted). */
export function invalidateMemo(state: object, prefix = ''): void {
  const entries = cache.get(state);
  if (entries === undefined) return;
  for (const key of [...entries.keys()]) if (key.startsWith(prefix)) entries.delete(key);
}
